import { Component, Input, OnInit, inject } from '@angular/core';
import { ToastService } from '@black/ui';
import { QuizService } from '../../shared/core/services/quiz.service';
import {
  Quiz,
  QuizQuestion,
  QuestionType,
  QuizSubmission,
  QuestionBankItem,
} from '../../shared/models/quiz.model';
import { LiveClass } from '../../shared/core/state/live-class/models/live-class';
import { SessionSelection } from '../../shared/component/session-picker/session-picker.component';

@Component({
  selector: 'black-instructor-quizzes',
  standalone: false,
  templateUrl: './quizzes.component.html',
})
export class QuizzesComponent implements OnInit {
  private quizService = inject(QuizService);
  private toastService = inject(ToastService);

  @Input() selectedLiveClassId: string | null = null;
  @Input() selectedLiveClass: LiveClass | null = null;
  @Input() editModeEnabled = true;

  quizzes: Quiz[] = [];
  loading = false;

  // Selected session for new quiz
  selectedSession: SessionSelection | null = null;

  // Quiz Builder State
  quizTitle = '';
  quizDescription = '';
  quizDuration = 0;
  quizPassingMarks = 0;
  quizMaxAttempts = 1;
  quizAvailableFrom = '';
  quizAvailableTo = '';
  quizQuestions: QuizQuestion[] = [];

  // Active question being added
  currentQuestionText = '';
  currentQuestionType = QuestionType.MULTIPLE_CHOICE;
  currentQuestionMarks = 1;
  saveCurrentToBank = false;
  currentOptions: { id: string; text: string; isCorrect: boolean }[] = [
    { id: 'opt_1', text: '', isCorrect: true },
    { id: 'opt_2', text: '', isCorrect: false },
  ];

  // Question Bank State
  questionBank: QuestionBankItem[] = [];
  loadingQuestionBank = false;
  selectedBankQuestionIds: Set<string> = new Set();
  bankSearchQuery = '';

  // Submissions modal state
  selectedQuizForSubmissions: Quiz | null = null;
  submissions: QuizSubmission[] = [];
  loadingSubmissions = false;

  ngOnInit(): void {
    this.fetchQuizzes();
    this.fetchQuestionBank();
  }

  fetchQuizzes(): void {
    if (!this.selectedLiveClassId) return;
    this.loading = true;
    this.quizService.getQuizzesByLiveClass(this.selectedLiveClassId).subscribe({
      next: (res) => {
        this.quizzes = res;
        this.loading = false;
      },
      error: () => {
        this.toastService.showToast('Failed to load quizzes', 'error');
        this.loading = false;
      },
    });
  }

  fetchQuestionBank(): void {
    this.loadingQuestionBank = true;
    this.quizService.getQuestionBank().subscribe({
      next: (items) => {
        this.questionBank = items;
        this.loadingQuestionBank = false;
      },
      error: () => {
        this.loadingQuestionBank = false;
      },
    });
  }

  onSessionSelected(selection: SessionSelection): void {
    this.selectedSession = selection;
  }

  addOption(): void {
    const nextId = `opt_${this.currentOptions.length + 1}`;
    this.currentOptions.push({ id: nextId, text: '', isCorrect: false });
  }

  removeOption(index: number): void {
    if (this.currentOptions.length > 2) {
      this.currentOptions.splice(index, 1);
    }
  }

  setCorrectOption(index: number): void {
    this.currentOptions.forEach((opt, idx) => {
      opt.isCorrect = idx === index;
    });
  }

  addQuestionToQuiz(): void {
    if (!this.currentQuestionText.trim()) {
      this.toastService.showToast('Please enter question text', 'error');
      return;
    }

    const validOptions = this.currentOptions.filter((opt) => opt.text.trim());
    if (validOptions.length < 2) {
      this.toastService.showToast('Please provide at least 2 options', 'error');
      return;
    }

    if (!validOptions.some((opt) => opt.isCorrect)) {
      validOptions[0].isCorrect = true;
    }

    const newQuestion: QuizQuestion = {
      id: `q_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      question: this.currentQuestionText.trim(),
      type: this.currentQuestionType,
      marks: Number(this.currentQuestionMarks) || 1,
      options: validOptions,
    };

    this.quizQuestions.push(newQuestion);

    // Save to Question Bank if requested
    if (this.saveCurrentToBank) {
      this.quizService
        .saveToQuestionBank({
          question: newQuestion.question,
          type: newQuestion.type,
          options: newQuestion.options,
          marks: newQuestion.marks,
        })
        .subscribe({
          next: (savedItem) => {
            this.questionBank.unshift(savedItem);
            this.toastService.showToast('Question saved to bank', 'success');
          },
        });
    }

    // Reset question form
    this.currentQuestionText = '';
    this.currentQuestionMarks = 1;
    this.saveCurrentToBank = false;
    this.currentOptions = [
      { id: 'opt_1', text: '', isCorrect: true },
      { id: 'opt_2', text: '', isCorrect: false },
    ];
  }

  removeQuestion(index: number): void {
    this.quizQuestions.splice(index, 1);
  }

  // Question Bank Import Logic
  toggleBankQuestionSelection(id: string): void {
    if (this.selectedBankQuestionIds.has(id)) {
      this.selectedBankQuestionIds.delete(id);
    } else {
      this.selectedBankQuestionIds.add(id);
    }
  }

  importSelectedQuestions(closeModal: () => void): void {
    const selected = this.questionBank.filter((item) =>
      this.selectedBankQuestionIds.has(item._id),
    );

    selected.forEach((item) => {
      this.quizQuestions.push({
        id: `q_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
        question: item.question,
        type: item.type,
        marks: item.marks || 1,
        options: item.options ? [...item.options] : [],
        explanation: item.explanation,
      });
    });

    this.selectedBankQuestionIds.clear();
    this.toastService.showToast(
      `Imported ${selected.length} question(s) from bank`,
      'success',
    );
    closeModal();
  }

  deleteFromBank(id: string): void {
    if (!confirm('Remove this question from question bank?')) return;
    this.quizService.deleteFromQuestionBank(id).subscribe({
      next: () => {
        this.questionBank = this.questionBank.filter((item) => item._id !== id);
        this.toastService.showToast('Question removed from bank', 'success');
      },
    });
  }

  get filteredQuestionBank(): QuestionBankItem[] {
    if (!this.bankSearchQuery.trim()) return this.questionBank;
    const q = this.bankSearchQuery.toLowerCase();
    return this.questionBank.filter((item) =>
      item.question.toLowerCase().includes(q),
    );
  }

  resetQuizForm(): void {
    this.quizTitle = '';
    this.quizDescription = '';
    this.quizDuration = 0;
    this.quizPassingMarks = 0;
    this.quizMaxAttempts = 1;
    this.quizAvailableFrom = '';
    this.quizAvailableTo = '';
    this.quizQuestions = [];
    this.selectedSession = null;
    this.currentQuestionText = '';
    this.saveCurrentToBank = false;
    this.currentOptions = [
      { id: 'opt_1', text: '', isCorrect: true },
      { id: 'opt_2', text: '', isCorrect: false },
    ];
  }

  saveQuiz(isPublished: boolean, closeModal: () => void): void {
    if (!this.quizTitle.trim()) {
      this.toastService.showToast('Please enter a quiz title', 'error');
      return;
    }
    if (this.quizQuestions.length === 0) {
      this.toastService.showToast('Please add at least one question', 'error');
      return;
    }

    const payload: Partial<Quiz> = {
      title: this.quizTitle.trim(),
      description: this.quizDescription.trim() || undefined,
      liveClass: this.selectedLiveClassId!,
      meetingId: this.selectedSession?.meetingId ?? undefined,
      sessionDate: this.selectedSession?.sessionDate ?? undefined,
      durationMinutes: Number(this.quizDuration) || 0,
      passingMarks: Number(this.quizPassingMarks) || 0,
      maxAttempts: Number(this.quizMaxAttempts) || 1,
      availableFrom: this.quizAvailableFrom ? new Date(this.quizAvailableFrom) : undefined,
      availableTo: this.quizAvailableTo ? new Date(this.quizAvailableTo) : undefined,
      questions: this.quizQuestions,
      isPublished,
    };

    this.quizService.createQuiz(payload).subscribe({
      next: (created) => {
        this.quizzes.unshift(created);
        this.toastService.showToast(
          isPublished ? 'Quiz published successfully' : 'Quiz saved as draft',
          'success',
        );
        this.resetQuizForm();
        closeModal();
      },
      error: () => {
        this.toastService.showToast('Failed to create quiz', 'error');
      },
    });
  }

  togglePublish(quiz: Quiz): void {
    const updated = !quiz.isPublished;
    this.quizService.updateQuiz(quiz._id, { isPublished: updated }).subscribe({
      next: () => {
        quiz.isPublished = updated;
        this.toastService.showToast(
          updated ? 'Quiz published' : 'Quiz moved to draft',
          'success',
        );
      },
      error: () => {
        this.toastService.showToast('Failed to update status', 'error');
      },
    });
  }

  deleteQuiz(quizId: string): void {
    if (!confirm('Are you sure you want to delete this quiz?')) return;
    this.quizService.deleteQuiz(quizId).subscribe({
      next: () => {
        this.quizzes = this.quizzes.filter((q) => q._id !== quizId);
        this.toastService.showToast('Quiz deleted successfully', 'success');
      },
      error: () => {
        this.toastService.showToast('Failed to delete quiz', 'error');
      },
    });
  }

  viewSubmissions(quiz: Quiz): void {
    this.selectedQuizForSubmissions = quiz;
    this.loadingSubmissions = true;
    this.quizService.getSubmissionsForQuiz(quiz._id).subscribe({
      next: (subs) => {
        this.submissions = subs;
        this.loadingSubmissions = false;
      },
      error: () => {
        this.toastService.showToast('Failed to load submissions', 'error');
        this.loadingSubmissions = false;
      },
    });
  }
}
